import React, { useState } from 'react';
import { Link2, Check } from 'lucide-react';

/**
 * CopyLinkButton — compact "bookmark-this-view" pill.
 * Copies window.location.href to the clipboard so users can share the exact
 * tab/filter/dashboard they're looking at. Uses the Clipboard API with a
 * legacy fallback via a hidden textarea + document.execCommand('copy').
 *
 * Place anywhere; it floats unobtrusively on the right-hand side of the page.
 */
export default function CopyLinkButton({ className = '' }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (!url) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Silently ignore — clipboard blocked by browser.
    }
  };

  return (
    <button
      type="button"
      data-testid="copy-link-btn"
      onClick={copy}
      title="Copy link to this view"
      aria-label="Copy link to this view"
      className={
        'inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md ' +
        'border border-border/70 bg-background/70 backdrop-blur-sm text-muted-foreground ' +
        'hover:text-foreground hover:border-border transition-colors ' +
        className
      }
    >
      {copied ? (
        <>
          <Check className="w-3.5 h-3.5 text-emerald-500" />
          <span className="text-emerald-600 dark:text-emerald-400">Link copied</span>
        </>
      ) : (
        <>
          <Link2 className="w-3.5 h-3.5" />
          <span>Copy link</span>
        </>
      )}
    </button>
  );
}
